import * as React from "react";
import { Tooltip, Position } from "@blueprintjs/core";
import Pagination from "react-js-pagination";
import Flexbox from "flexbox-react";
import Avatar from 'react-avatar';

const getStatusStyle = status => {
    const value = String(status || "").toLowerCase();

    if (
        value.includes("completed") ||
        value.includes("released")
    ) {
        return {
            backgroundColor: "rgba(20, 184, 90, 0.12)",
            color: "#15803d"
        };
    }

    if (value.includes("progress")) {
        return {
            backgroundColor: "rgba(59, 130, 246, 0.12)",
            color: "#2563eb"
        };
    }

    if (
        value.includes("review") ||
        value.includes("pending") || value.includes("not started")
    ) {
        return {
            backgroundColor: "rgba(217, 155, 0, 0.12)",
            color: "#b7791f"
        };
    }

    return {
        backgroundColor: "rgba(112, 112, 112, 0.12)",
        color: "#4a5568"
    };
};

const RatingComparison = ({
    selfComparison,
    otherComparison
}) => {
    const getRatingPercentage = rating => {
        const value = Number(rating);

        if (isNaN(value)) {
            return 0;
        }

        return Math.min(
            100,
            Math.max(0, (value / 5) * 100)
        );
    };

    const selfPercentage = getRatingPercentage(
        selfComparison
    );

    const otherPercentage = getRatingPercentage(
        otherComparison
    );

    const segments = [0, 1, 2, 3, 4];

    return (
        <div className="review-360-rating-comparison">
            {/* Self */}
            <div className="review-360-rating-row">
                {segments.map(index => {
                    const segmentStart = index * 20;
                    const segmentEnd = (index + 1) * 20;

                    const fillPercentage =
                        Math.min(
                            100,
                            Math.max(
                                0,
                                ((selfPercentage - segmentStart) /
                                    (segmentEnd - segmentStart)) *
                                100
                            )
                        );

                    return (
                        <div
                            key={`self-${index}`}
                            className="review-360-rating-segment"
                        >
                            <div
                                className="review-360-self-bar"
                                style={{
                                    width: `${fillPercentage}%`
                                }}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Others */}
            <div className="review-360-rating-row">
                {segments.map(index => {
                    const segmentStart = index * 20;
                    const segmentEnd = (index + 1) * 20;

                    const fillPercentage =
                        Math.min(
                            100,
                            Math.max(
                                0,
                                ((otherPercentage - segmentStart) /
                                    (segmentEnd - segmentStart)) *
                                100
                            )
                        );

                    return (
                        <div
                            key={`other-${index}`}
                            className="review-360-rating-segment"
                        >
                            <div
                                className="review-360-other-bar"
                                style={{
                                    width: `${fillPercentage}%`
                                }}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const Review360ReportTable = ({
    data = [],
    isLoading,
    currentPage,
    perPage,
    totalCount,
    totalPages,
    onPageChange,
    onPerPageChange,
    onRowClick,
    onRelease
}) => {
    return (
        <div className="review-360-table-card">
            <div className="review-360-table-scroll">
                <table className="review-360-table">
                    <thead>
                        <tr>
                            <th className="review-360-employee-column">
                                Employee Name
                            </th>

                            <th className="review-360-rating-column">
                                <div className="review-360-rating-header">
                                    <span>
                                        Rating Comparison
                                    </span>

                                    <div className="review-360-legend">
                                        <span>
                                            <span className="review-360-self-dot" />
                                            Self
                                        </span>
                                        <span>
                                            <span className="review-360-other-dot" />
                                            Others
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th>Self Assessment</th>
                            <th> Other's Assessment </th>
                            <th> Overall Status </th>
                            <th>Release</th>
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td
                                    colSpan={6}
                                    style={{
                                        textAlign: "center",
                                        padding: "30px"
                                    }}
                                >
                                    <img
                                        style={{ height: '35px', width: '35px' }}
                                        src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif"
                                        alt="Loading..."
                                    />
                                </td>
                            </tr>
                        ) : data.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={6}
                                    style={{
                                        textAlign: "center",
                                        padding: "30px"
                                    }}
                                >
                                    No data found
                                </td>
                            </tr>
                        ) : (
                            data.map((row, index) => {
                               
                                const statusStyle = getStatusStyle(row.status);
                                const isCompleted = row.status === "Completed";
                                const isReleased = Number(row.release) === 1;
                                const canRelease = isCompleted && !isReleased;
                                const canOpen = isCompleted && isReleased;
                               

                                return (
                                    <tr
                                        key={row.id || `row-${index}`}
                                        className={`review-360-table-row ${canOpen ? "review-360-row-clickable" : ""
                                            }`}
                                        onClick={() => {
                                            if (canOpen && onRowClick) {
                                                onRowClick(row);
                                            }
                                        }}
                                    >
                                        {/* Employee */}
                                        <td>
                                            <div className="employee-cell">
                                                <Avatar
                                                    src={row?.imageFileName}
                                                    size={36}
                                                    round={true}
                                                />

                                                <div className="employee-info">
                                                    <div className="employee-name">
                                                        {row?.name}
                                                    </div>

                                                    {/* <div className="employee-designation">
                                                        {row?.email}
                                                    </div> */}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Rating Comparison */}
                                        <td>

                                            <RatingComparison
                                                selfComparison={
                                                    row.selfRating
                                                }
                                                otherComparison={
                                                    row.otherRating
                                                }
                                            />
                                        </td>

                                        {/* Self */}
                                        <td>
                                            <span className="review-360-self-rating">
                                                {row.selfRating ?? "-"}
                                            </span>
                                        </td>

                                        {/* Others */}
                                        <td>
                                            <span className="review-360-other-rating">
                                                {row.otherRating ?? "-"}
                                            </span>
                                        </td>

                                        {/* Status */}
                                        <td>
                                            <span
                                                className="review-360-status"
                                                style={{
                                                    backgroundColor: statusStyle.backgroundColor,
                                                    color: statusStyle.color,
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {row.status || "-"}
                                            </span>
                                        </td>
                                        <td>
                                            {!isCompleted ? (
                                                // Not completed
                                                <span className="review-360-release-placeholder">
                                                    -
                                                </span>
                                            ) : isReleased ? (
                                                // Completed + Released
                                                <button
                                                    type="button"
                                                    className="review-360-release"
                                                    disabled
                                                    style={{
                                                        border: "1px solid #15803d",
                                                        backgroundColor: "rgba(20, 184, 90, 0.08)",
                                                        color: "#15803d",
                                                        padding: "6px 14px",
                                                        borderRadius: "6px",
                                                        fontSize: "13px",
                                                        fontWeight: 500,
                                                        cursor: "default",
                                                        opacity: 0.7
                                                    }}
                                                >
                                                    Released
                                                </button>
                                            ) : (
                                                // Completed + Not Released
                                                <button
                                                    type="button"
                                                    className="review-360-release"
                                                    onClick={(e) => {
                                                        e.stopPropagation();

                                                        if (onRelease) {
                                                            onRelease(row);
                                                        }
                                                    }}
                                                    style={{
                                                        border: "1px solid #1870d5",
                                                        backgroundColor: "#fff",
                                                        color: "#1870d5",
                                                        padding: "6px 14px",
                                                        borderRadius: "6px",
                                                        fontSize: "13px",
                                                        fontWeight: 500,
                                                        cursor: "pointer"
                                                    }}
                                                >
                                                    Release
                                                </button>
                                            )}
                                        </td>

                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {!isLoading &&
                data.length > 0 &&
                totalCount > 0 && (
                    <div className="review-360-table-footer">
                        <Flexbox
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                        >
                            <span>
                                Showing{" "}
                                {Math.min(
                                    (currentPage - 1) * perPage + 1,
                                    totalCount
                                )}{" "}
                                to{" "}
                                {Math.min(
                                    currentPage * perPage,
                                    totalCount
                                )}{" "}
                                of {totalCount} entries
                            </span>

                            <Flexbox alignItems="center">
                                <Pagination
                                    activePage={currentPage}
                                    itemsCountPerPage={perPage}
                                    totalItemsCount={totalCount}
                                    pageRangeDisplayed={Math.min(
                                        5,
                                        totalPages || 5
                                    )}
                                    onChange={onPageChange}
                                    disabledClass="bp3-disabled"
                                    innerClass="bp3-button-group bp3-minimal bp3-pagination"
                                    itemClass="bp3-button"
                                    activeClass="bp3-active bp3-pagination-active"
                                    linkClass="no-underline"
                                    prevPageText={
                                        <span className="bp3-icon-chevron-left" />
                                    }
                                    nextPageText={
                                        <span className="bp3-icon-chevron-right" />
                                    }
                                />

                                <span
                                    style={{
                                        marginLeft: "25px",
                                        marginRight: "8px"
                                    }}
                                >
                                    Show
                                </span>

                                <select
                                    value={perPage}
                                    onChange={onPerPageChange}
                                    className="bp3-input"
                                    style={{
                                        width: 65
                                    }}
                                >
                                    {[10, 25, 40, 100].map(size => (
                                        <option
                                            key={size}
                                            value={size}
                                        >
                                            {size}
                                        </option>
                                    ))}
                                </select>

                                <span
                                    style={{
                                        marginLeft: "8px"
                                    }}
                                >
                                    per page
                                </span>
                            </Flexbox>
                        </Flexbox>
                    </div>
                )}
        </div>
    );
};

export default Review360ReportTable;